import type { LazyQueryHookOptions } from '@apollo/react-hooks'
import type { SearchQuotesQuery } from '@paintscout/api'
import { useSearchQuotesLazyQuery } from '@paintscout/api'
import { useState } from 'react'

/**
 * A helper hook to simplify managing quote search and pagination
 */
export function useQuoteSearch(
  options: { query?: string; fetchPolicy?: string; limit?: number; prevBookmark?: string; prevSort?: string[] } & Omit<
    LazyQueryHookOptions,
    'query'
  > = {}
) {
  const { limit, query, fetchPolicy = 'cache-first', prevBookmark, prevSort, ...queryOptions } = options
  const [bookmark, setBookmark] = useState<string>(prevBookmark ?? undefined)
  const [lastQuery, setLastQuery] = useState(query)
  const [lastFilters, setLastFilters] = useState<unknown>()
  const [sort, setSort] = useState<string[]>(prevSort ?? [])

  const [searchQuotes, queryResults] = useSearchQuotesLazyQuery({
    fetchPolicy,
    ...(queryOptions as any), // ? this ts error is stupid
    onCompleted: (data) => {
      if (data?.searchQuotes) {
        setBookmark(data.searchQuotes.bookmark)
      }
      if (queryOptions.onCompleted) {
        queryOptions.onCompleted(data)
      }
    }
  })

  const search = ({ ...args }: { query: string; filters?: unknown; limit?: number; sort?: string[] }) => {
    setLastQuery(args.query)
    setLastFilters(args.filters)

    if (args.sort) {
      setSort(args.sort)
    }

    searchQuotes({
      variables: {
        limit,
        ...args,
        sort: args.sort || sort
      }
    })
  }

  const fetchMore = () => {
    return queryResults.fetchMore({
      variables: { query: lastQuery, filters: lastFilters, sort, limit, bookmark },
      updateQuery: (previousResult: SearchQuotesQuery, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.searchQuotes) {
          return previousResult
        }

        return {
          ...previousResult,
          ...fetchMoreResult,
          searchQuotes: {
            ...previousResult.searchQuotes,
            ...fetchMoreResult.searchQuotes,
            bookmark: fetchMoreResult.searchQuotes.bookmark,
            quotes: [...previousResult.searchQuotes.quotes, ...fetchMoreResult.searchQuotes.quotes]
          }
        }
      }
    })
  }

  const newQueryResults = { ...queryResults, fetchMore }

  return [search, newQueryResults] as [typeof search, typeof newQueryResults]
}
